import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { ITextElement, IPunctElement } from '../models/interfaces';

/**
 * Command to assign a color to selected transcript elements
 */
export class TranscriptAssignColorCommand extends AbstractTranscriptEditorCommand {
  private elementIds: string[];
  private color: string;
  private previousColors: Map<string, string | undefined> = new Map();

  constructor(
    editor: TranscriptEditorCore,
    elementIds: string[],
    color: string,
    userId: string
  ) {
    super(`Assign color ${color} to ${elementIds.length} element(s)`, editor);
    this.elementIds = elementIds;
    this.color = color;
    
    // Store metadata for debugging
    this.setMetadata('elementIds', elementIds);
    this.setMetadata('color', color);
    this.setMetadata('userId', userId);
  }

  async execute(): Promise<boolean> {
    try {
      const container = this.editor.getContainer();
      if (!container) return false;

      // Process each element
      for (const elementId of this.elementIds) {
        // Find the element across all segments and paragraphs
        for (const segment of container.segments) {
          for (const paragraph of segment.paragraphs) {
            const element = paragraph.elements.find(e => e.id === elementId);
            if (element) {
              // Store the previous color for undo
              this.previousColors.set(elementId, element.color);
              
              // Assign the new color
              element.color = this.color;
              
              // Increment counters to trigger reactivity
              element.counter++;
              paragraph.counter++;
              
              // Record the operation
              this.editor.recordOperation({
                type: 'edit',
                data: {
                  elementId,
                  color: this.color,
                  previousColor: this.previousColors.get(elementId),
                  action: 'color_assigned'
                },
                userId: this.getMetadata('userId'),
                timestamp: Date.now()
              });
            }
          }
        }
      }

      // Notify observers about the color change
      this.editor.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_MODIFIED, {
        elementIds: this.elementIds,
        changeType: 'color_assigned',
        color: this.color,
        container: container
      });

      return true;
    } catch (error) {
      console.error('Error executing TranscriptAssignColorCommand:', error);
      return false;
    }
  }

  async undo(): Promise<boolean> {
    try {
      const container = this.editor.getContainer();
      if (!container) return false;

      // Restore previous colors
      for (const [elementId, previousColor] of this.previousColors) {
        let containerChanged = false;
        for (const segment of container.segments) {
          let segmentChanged = false;
          for (const paragraph of segment.paragraphs) {
            const element = paragraph.elements.find(e => e.id === elementId);
            if (element) {
              element.color = previousColor;
              
              // Increment counters to trigger reactivity
              element.counter++;
              paragraph.counter++;
              segmentChanged = true;
              containerChanged = true;
            }
          }
          if (segmentChanged) {
            segment.counter++;
          }
        }
        if (containerChanged) {
          container.counter++;
        }
      }

      // Notify observers about the color restoration
      this.editor.notifyObservers(TranscriptEditorChangeType.TRANSCRIPT_MODIFIED, {
        elementIds: this.elementIds,
        changeType: 'color_assigned',
        container: container
      });

      return true;
    } catch (error) {
      console.error('Error undoing TranscriptAssignColorCommand:', error);
      return false;
    }
  }
}